import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Bell, 
  CheckCircle, 
  Gift, 
  Users, 
  Coins,
  Lock,
  Unlock,
  Check,
  HelpCircle,
  Trophy,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Activity = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: getAuthHeaders()
      });
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return <Gift className="text-[#2272C5]" size={18} />;
      case 'group_join':
        return <Users className="text-[#0B1F3A]" size={18} />;
      case 'contribution':
        return <Coins className="text-[#2272C5]" size={18} />;
      case 'payout':
        return <CheckCircle className="text-[#2272C5]" size={18} />;
      case 'security_lock':
        return <Lock className="text-amber-500" size={18} />;
      case 'security_release':
        return <Unlock className="text-[#2272C5]" size={18} />;
      case 'group_complete':
        return <Trophy className="text-[#2272C5]" size={18} />;
      case 'low_balance_alert':
        return <AlertTriangle className="text-red-600" size={18} />;
      default:
        return <Bell className="text-slate-500" size={18} />;
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'welcome':
      case 'payout':
      case 'security_release':
      case 'group_complete':
        return 'bg-[#2272C5]/10';
      case 'group_join':
        return 'bg-[#0B1F3A]/10';
      case 'contribution':
        return 'bg-blue-100';
      case 'security_lock':
        return 'bg-amber-100';
      case 'low_balance_alert':
        return 'bg-red-100';
      default:
        return 'bg-slate-100';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 w-full rounded-2xl" />
            ))}
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="activity-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0B1F3A]">Activity</h1>
            <p className="text-sm text-slate-500">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/help')}
              className="rounded-full"
              data-testid="help-button"
            >
              <HelpCircle size={14} className="mr-1" />
              Help
            </Button>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={markAllAsRead}
                className="rounded-full"
                data-testid="mark-all-read-button"
              >
                <Check size={14} className="mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
              <Bell size={24} className="text-slate-400" />
            </div>
            <h3 className="font-semibold text-[#0B1F3A] mb-1">No notifications yet</h3>
            <p className="text-sm text-slate-500">
              We'll notify you about your savings activities
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id}
                className={`glass-card notification-item cursor-pointer ${
                  !notification.is_read ? 'notification-unread' : ''
                }`}
                onClick={() => {
                  if (!notification.is_read) markAsRead(notification.id);
                  if (notification.url && notification.url !== '/') navigate(notification.url);
                }}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl ${getNotificationBg(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-[#0B1F3A]">{notification.title}</p>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">
                        {notification.message}
                      </p>
                      {!notification.is_read && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-[#2272C5]/10 text-[#2272C5]">
                            New
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Activity;
